// src/pages/Checkout/PaymentMethod.jsx
import React, { useEffect, useMemo, useState } from "react";
import { PaystackButton } from "react-paystack";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { useCart } from "../../hooks/CartContext";
import { useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaChevronRight,
  FaCreditCard,
  FaMoneyBillWave,
  FaUniversity,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaShieldAlt,
  FaBoxOpen,
} from "react-icons/fa";

const PaymentMethod = () => {
  const navigate = useNavigate();
  const token = Cookies.get("gameport");
  const { clearCart } = useCart();

  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(`ref-${Date.now()}`);
  const [checkoutData, setCheckoutData] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState("Paystack");

  useEffect(() => {
    const saved = sessionStorage.getItem("checkout_payload");

    if (!saved) {
      toast.error("No checkout data found");
      navigate("/cart");
      return;
    }

    try {
      setCheckoutData(JSON.parse(saved));
    } catch {
      toast.error("Invalid checkout data");
      navigate("/cart");
    }
  }, [navigate]);

  const totalPrice = useMemo(() => {
    return Number(checkoutData?.totalPrice || 0);
  }, [checkoutData]);

  const items = useMemo(() => {
    return checkoutData?.items || [];
  }, [checkoutData]);

  const totalItems = useMemo(() => {
    return items.reduce((acc, item) => acc + Number(item.quantity || 0), 0);
  }, [items]);

  const placeOrder = async ({ paymentMethod, referenceValue }) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/order/place-order`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        items: checkoutData.items,
        shippingAddress: checkoutData.shippingAddress,
        paymentMethod,
        reference: referenceValue,
      }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      throw new Error(data.message || "Order failed");
    }

    return data;
  };

  const paystackConfig = useMemo(() => {
    return {
      email: checkoutData?.shippingAddress?.email || "",
      amount: Math.round(totalPrice * 100),
      publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
      currency: "NGN",
      reference,
      metadata: {
        customer_name: checkoutData?.shippingAddress?.name,
        phone: checkoutData?.shippingAddress?.telephone,
      },
    };
  }, [checkoutData, totalPrice, reference]);

  const onPaystackSuccess = async () => {
    try {
      setLoading(true);

      await placeOrder({
        paymentMethod: "Paystack",
        referenceValue: reference,
      });

      clearCart();
      sessionStorage.removeItem("checkout_payload");
      setReference(`ref-${Date.now()}`);

      toast.success("Payment successful! Order placed.");
      navigate("/profile");
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const onPaystackClose = () => {
    toast("Payment cancelled");
  };

  const handleBankTransferOrder = async () => {
    try {
      setLoading(true);

      const bankReference = `BANK-${Date.now()}`;

      await placeOrder({
        paymentMethod: "Bank Transfer",
        referenceValue: bankReference,
      });

      clearCart();
      sessionStorage.removeItem("checkout_payload");

      toast.success("Order placed. Awaiting bank transfer confirmation.");
      navigate("/profile");
    } catch (err) {
      toast.error(err.message || "Failed to place order");
    } finally {
      setLoading(false);
    }
  };

  const handleCashOnDeliveryOrder = async () => {
    try {
      setLoading(true);

      const codReference = `COD-${Date.now()}`;

      await placeOrder({
        paymentMethod: "Cash on Delivery",
        referenceValue: codReference,
      });

      clearCart();
      sessionStorage.removeItem("checkout_payload");

      toast.success("Order placed successfully. Pay on delivery.");
      navigate("/profile");
    } catch (err) {
      toast.error(err.message || "Failed to place order");
    } finally {
      setLoading(false);
    }
  };

  const methodCardBase =
    "rounded-2xl border p-4 sm:p-5 transition cursor-pointer";
  const methodCardActive = "border-red bg-red/5 shadow-sm";
  const methodCardInactive = "border-gray-200 bg-white hover:border-gray-300";

  if (!checkoutData) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <p className="text-gray-600">Loading checkout...</p>
          </div>
        </div>
      </div>
    );
  }

  const shipping = checkoutData.shippingAddress || {};

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        {/* Top */}
        <div className="mb-6 flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            Payment Method
          </h1>
          <p className="text-sm text-gray-500">
            Choose how you want to pay for your order.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr,1fr]">
          {/* Left */}
          <div className="space-y-5">
            {/* Delivery Info */}
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="text-lg font-bold text-gray-900">
                  Delivery Details
                </h2>
              </div>

              <div className="space-y-4 p-5">
                <div className="flex items-start gap-3">
                  <FaMapMarkerAlt className="mt-1 text-red" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      {shipping.name || "Customer"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {shipping.address}, {shipping.city}, {shipping.country}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-3">
                    <FaPhoneAlt className="text-red" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Telephone
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {shipping.telephone || "—"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-3">
                    <FaEnvelope className="text-red" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Email
                      </p>
                      <p className="text-sm font-medium text-gray-900 break-all">
                        {shipping.email || "—"}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/cart")}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-red"
                >
                  Edit cart or shipping details <FaChevronRight size={12} />
                </button>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="text-lg font-bold text-gray-900">
                  Select a Payment Method
                </h2>
              </div>

              <div className="space-y-4 p-5">
                {/* Paystack */}
                <div
                  onClick={() => setSelectedMethod("Paystack")}
                  className={`${methodCardBase} ${
                    selectedMethod === "Paystack"
                      ? methodCardActive
                      : methodCardInactive
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-3">
                      <div className="mt-1 rounded-full bg-red/10 p-3 text-red">
                        <FaCreditCard />
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-900">
                            Card / Bank / USSD
                          </h3>
                          {selectedMethod === "Paystack" && (
                            <FaCheckCircle className="text-red" />
                          )}
                        </div>

                        <p className="mt-1 text-sm text-gray-600">
                          Pay instantly with card, transfer, USSD, or other
                          Paystack supported options.
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedMethod === "Paystack" && (
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <PaystackButton
                        className={`w-full rounded-xl py-3 font-semibold transition ${
                          loading
                            ? "cursor-not-allowed bg-gray-300 text-gray-700"
                            : "bg-red text-white hover:opacity-90"
                        }`}
                        {...paystackConfig}
                        text={loading ? "Processing..." : "Continue to Payment"}
                        onSuccess={onPaystackSuccess}
                        onClose={onPaystackClose}
                        disabled={loading}
                      />

                      <p className="mt-2 text-xs text-gray-500">
                        You will be redirected to a secure Paystack payment popup.
                      </p>
                    </div>
                  )}
                </div>

                {/* Bank Transfer */}
                <div
                  onClick={() => setSelectedMethod("Bank Transfer")}
                  className={`${methodCardBase} ${
                    selectedMethod === "Bank Transfer"
                      ? methodCardActive
                      : methodCardInactive
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-3">
                      <div className="mt-1 rounded-full bg-red/10 p-3 text-red">
                        <FaUniversity />
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-900">
                            Bank Transfer
                          </h3>
                          {selectedMethod === "Bank Transfer" && (
                            <FaCheckCircle className="text-red" />
                          )}
                        </div>

                        <p className="mt-1 text-sm text-gray-600">
                          Place your order and complete payment with a direct bank transfer.
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedMethod === "Bank Transfer" && (
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <div className="space-y-3 text-sm text-gray-700">
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                          <p className="font-semibold text-gray-900">Providus Bank</p>
                          <p>Account Name: Gameportng Global Ltd</p>
                          <p>Account Number: 1309235160</p>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                          <p className="font-semibold text-gray-900">GTBank</p>
                          <p>Account Name: Gameportng Global Ltd</p>
                          <p>Account Number: 0657346459</p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleBankTransferOrder}
                        disabled={loading}
                        className={`mt-4 w-full rounded-xl py-3 font-semibold transition ${
                          loading
                            ? "cursor-not-allowed bg-gray-300 text-gray-700"
                            : "bg-red text-white hover:opacity-90"
                        }`}
                      >
                        {loading ? "Placing Order..." : "Place Order"}
                      </button>

                      <p className="mt-2 text-xs text-gray-500">
                        Your order will remain pending until payment is confirmed.
                      </p>
                    </div>
                  )}
                </div>

                {/* Cash on Delivery */}
                <div
                  onClick={() => setSelectedMethod("Cash on Delivery")}
                  className={`${methodCardBase} ${
                    selectedMethod === "Cash on Delivery"
                      ? methodCardActive
                      : methodCardInactive
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-3">
                      <div className="mt-1 rounded-full bg-red/10 p-3 text-red">
                        <FaMoneyBillWave />
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-900">
                            Cash on Delivery
                          </h3>
                          {selectedMethod === "Cash on Delivery" && (
                            <FaCheckCircle className="text-red" />
                          )}
                        </div>

                        <p className="mt-1 text-sm text-gray-600">
                          Place your order now and pay when the order gets to you.
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedMethod === "Cash on Delivery" && (
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-700">
                        <p className="font-semibold text-gray-900">
                          Important Note
                        </p>
                        <p className="mt-1">
                          Cash on delivery may be available only for selected
                          locations and eligible orders.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={handleCashOnDeliveryOrder}
                        disabled={loading}
                        className={`mt-4 w-full rounded-xl py-3 font-semibold transition ${
                          loading
                            ? "cursor-not-allowed bg-gray-300 text-gray-700"
                            : "bg-red text-white hover:opacity-90"
                        }`}
                      >
                        {loading ? "Placing Order..." : "Confirm Cash on Delivery"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Protection / Notes */}
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <FaShieldAlt className="mt-1 text-red" />
                <div>
                  <h3 className="font-bold text-gray-900">Safe & Secure Checkout</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Your payment and delivery details are processed securely.
                  </p>
                </div>
              </div>

              <div className="mt-4 flex items-start gap-3">
                <FaBoxOpen className="mt-1 text-red" />
                <div>
                  <h3 className="font-bold text-gray-900">Order Confirmation</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    After placing your order, you will receive confirmation and invoice details.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Summary */}
          <div className="h-max rounded-2xl border border-gray-100 bg-white shadow-sm lg:sticky lg:top-6">
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 className="text-lg font-bold text-gray-900">Order Summary</h2>
            </div>

            <div className="space-y-4 p-5">
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div
                    key={`${item.product}-${index}`}
                    className="flex items-start justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-sm font-medium text-gray-900">
                        {item.name}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        Qty: {item.quantity}
                      </p>
                    </div>

                    <p className="shrink-0 text-sm font-semibold text-gray-900">
                      ₦
                      {Number(
                        Number(item.finalPrice || 0) * Number(item.quantity || 0)
                      ).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-4">
                <div className="mb-2 flex items-center justify-between text-sm text-gray-600">
                  <span>Items ({totalItems})</span>
                  <span>₦{Number(totalPrice).toLocaleString()}</span>
                </div>

                <div className="mb-2 flex items-center justify-between text-sm text-gray-600">
                  <span>Delivery Fee</span>
                  <span>Calculated later</span>
                </div>

                <div className="mt-4 flex items-center justify-between text-lg font-bold text-gray-900">
                  <span>Total</span>
                  <span>₦{Number(totalPrice).toLocaleString()}</span>
                </div>
              </div>

              <div className="rounded-xl bg-gray-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Selected Payment Method
                </p>
                <p className="mt-1 text-sm font-bold text-gray-900">
                  {selectedMethod}
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate("/cart")}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 font-semibold text-gray-800 transition hover:bg-gray-50"
              >
                Back to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethod;